import React, { useEffect, useRef, useState } from 'react';
 
export default function Stats() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
 
  // Mouse Drag & Hover Variables
  const isMouseDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
 
  // Intersection Observer (Scroll Detection)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
 
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
 
    return () => observer.disconnect();
  }, []);
 
  // Slide Controls (Left / Right Buttons)
  const slide = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === 'left' ? -240 : 240;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
 
  // Drag Handlers
  const handleMouseDown = (e) => {
    isMouseDown.current = true;
    sliderRef.current.classList.add('active-drag');
    startX.current = e.pageX - sliderRef.current.offsetLeft;
    scrollLeft.current = sliderRef.current.scrollLeft;
  };
 
  const handleMouseLeaveOrUp = () => {
    isMouseDown.current = false;
    if (sliderRef.current) sliderRef.current.classList.remove('active-drag');
  };
 
  const handleMouseMove = (e) => {
    if (!isMouseDown.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;
    sliderRef.current.scrollLeft = scrollLeft.current - walk;
  };
 
  const statsData = [
    { num: '12+', label: 'International Medals', icon: '🏃' },
    { num: '2', label: 'Gold Medals', icon: '🥇' },
    { num: '7', label: 'Silver Medals', icon: '🥈' },
    { num: '3', label: 'Bronze Medals', icon: '🥉' },
    { num: '5+', label: 'International Championships', icon: '🌐' },
    { num: '100', label: 'Medals Ambitions', icon: '🚩' },
  ];
 
  // बिना गैप (Gapless Infinite Marquee) साठी डेटा दुप्पट केला आहे
  const marqueeData = [...statsData, ...statsData];
 
  return (
    <section 
      ref={sectionRef} 
      id="achievements" 
      style={styles.statsSection} 
      className={`py-4 py-lg-5 ${isVisible ? 'fade-in-down' : 'invisible-section'}`}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800;900&family=Poppins:wght@400;500;600;700&display=swap');
 
        .invisible-section { opacity: 0; }
 
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-40px); }
          to { opacity: 1; transform: translateY(0); }
        }
 
        .fade-in-down { animation: fadeInDown 0.8s ease-out forwards; }
 
        /* Slider Wrapper */
        .slider-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
 
        .stats-slider-container {
          display: flex;
          overflow-x: auto;
          scroll-behavior: smooth;
          padding: 15px 5px;
          -webkit-overflow-scrolling: touch;
          cursor: grab;
          scrollbar-width: none;
        }
 
        .stats-slider-container::-webkit-scrollbar {
          display: none;
        }
 
        .stats-slider-container.active-drag {
          cursor: grabbing;
          scroll-behavior: auto;
        }
 
        /* Continuous Marquee Track */
        .marquee-track {
          display: flex;
          gap: 16px;
          width: max-content;
          animation: marqueeScroll 20s linear infinite;
        }
 
        /* माऊस कार्ड्सवर गेल्यावर Marquee आपोआप थांबेल */
        .stats-slider-container:hover .marquee-track,
        .stats-slider-container.active-drag .marquee-track {
          animation-play-state: paused;
        }
 
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
 
        .stats-slide-item {
          flex: 0 0 200px;
        }
 
        .stat-card-anim {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          user-select: none;
        }
 
        .stat-card-anim:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
        }
 
        /* Nav Buttons Styling */
        .nav-btn {
          background: #ffffff;
          border: 2px solid #ff7700;
          color: #ff7700;
          min-width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
          z-index: 2;
          transition: all 0.2s ease;
          outline: none;
        }
 
        .nav-btn:hover {
          background: #ff7700;
          color: #ffffff;
          transform: scale(1.08);
        }
 
        @media (max-width: 575.98px) {
          .stats-slide-item {
            flex: 0 0 160px;
          }
          .nav-btn { 
            min-width: 32px;
            height: 32px;
          }
        }
      `}</style>
 
      <div className="container-fluid py-2 py-lg-3">
        {/* Title */}
        <div className="d-flex align-items-center justify-content-center mb-3 mb-md-4">
          <div style={styles.headingLineLeft}></div>
          <h5 style={styles.sectionHeading}>CAREER STATISTICS</h5>
          <div style={styles.headingLineRight}></div>
        </div>
 
        {/* Slider Wrapper */}
        <div className="slider-wrapper">
          {/* Left Arrow Button */}
          <button 
            className="nav-btn me-2 me-md-3" 
            onClick={() => slide('left')}
            aria-label="Previous Slide"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
 
          {/* Marquee Slider Container */}
          <div 
            ref={sliderRef}
            className="stats-slider-container text-center w-100"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeaveOrUp}
            onMouseUp={handleMouseLeaveOrUp}
            onMouseMove={handleMouseMove}
          >
            <div className="marquee-track">
              {marqueeData.map((stat, idx) => (
                <div key={idx} className="stats-slide-item">
                  <div className="stat-card-anim" style={styles.statCard}>
                    <div style={styles.statIcon}>{stat.icon}</div>
                    <div style={styles.statNum}>{stat.num}</div>
                    <div style={styles.statLabel}>{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
 
          {/* Right Arrow Button */}
          <button 
            className="nav-btn ms-2 ms-md-3" 
            onClick={() => slide('right')}
            aria-label="Next Slide"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
 
const styles = {
  statsSection: { 
    background: 'linear-gradient(135deg, #ffa24a 0%, #fff7ed 35%, #a1ff54 100%)',
    overflow: 'hidden',
    fontFamily: "'Poppins', sans-serif",
  },
  sectionHeading: { 
    fontWeight: '600', 
    color: '#000080',
    letterSpacing: '2px',
    fontSize: 'clamp(18px, 3.5vw, 24px)',
    margin: '0 15px',
    whiteSpace: 'nowrap',
    fontFamily: "'Montserrat', sans-serif",
  },
  headingLineLeft: {
    height: '4px',
    width: '60px',
    backgroundColor: '#ff7700',
    borderRadius: '2px',
  },
  headingLineRight: {
    height: '4px',
    width: '60px',
    backgroundColor: '#008000',
    borderRadius: '2px',
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px 8px',
    border: '1px solid #fed7aa', 
    borderBottom: '4px solid #ff7700',
    borderRight: '2px solid #ff7700',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)', 
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifycontent: 'center',
    alignItems: 'center',
  },
  statIcon: {
    fontSize: 'clamp(20px, 4vw, 26px)',
  },
  statNum: { 
    fontSize: 'clamp(20px, 4.5vw, 26px)', 
    fontWeight: '600', 
    color: '#0b192c', 
    margin: '4px 0',
    lineHeight: '1.2',
    fontFamily: "'Poppins', sans-serif",
  },
  statLabel: { 
    fontSize: '14px', 
    fontWeight: '600', 
    color: '#000000',
    letterSpacing: '0.5px',
    lineHeight: '1.10',
    fontFamily: "'Poppins', sans-serif",
  },
};
 